<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import Swal from "sweetalert2";
import {
  discardImportJob,
  downloadProblemRows,
  downloadTemplate,
  getColumns,
  getImportJob,
  getImportRows,
  listImportJobs,
  startImport,
  uploadImportFile,
} from "@/services/importService";

const route = useRoute();
const router = useRouter();

const ENTITIES = [
  { key: "category", label: "Categories", link: "/categorylist" },
  { key: "supplier", label: "Suppliers", link: "/supplierlist" },
  { key: "customer", label: "Customers", link: "/customerlist" },
  { key: "item", label: "Items", link: "/productlist" },
];

const STATUS = {
  NEW: { label: "New", color: "green" },
  EXISTS: { label: "Already exists", color: "blue" },
  DUPLICATE_IN_FILE: { label: "Duplicate in file", color: "orange" },
  ERROR: { label: "Error", color: "red" },
  IMPORTED: { label: "Imported", color: "success" },
  FAILED: { label: "Failed", color: "error" },
};

const RUNNING = ["UPLOADED", "VALIDATING", "IMPORTING"];

// ----- state -----------------------------------------------------------
const columnsByEntity = ref({});
const job = ref(null);
const recentJobs = ref([]);

const fileList = ref([]);
const csvEntity = ref(null);
const templateScope = ref("all");
const downloadingTemplate = ref(false);
const uploading = ref(false);
const uploadPercent = ref(0);
const busy = ref(false);

const activeEntity = ref("item");
const statusFilter = ref("ALL");
const search = ref("");
const rows = ref([]);
const rowsLoading = ref(false);
const pagination = reactive({ current: 1, pageSize: 50, total: 0 });

let pollTimer = null;

// ----- derived ---------------------------------------------------------
const selectedFile = computed(() => fileList.value[0] || null);
const isCsv = computed(() => /\.csv$/i.test(selectedFile.value?.name || ""));
const counts = computed(() => job.value?.counts || {});
const isRunning = computed(() => job.value && RUNNING.includes(job.value.status));
const isPreview = computed(() => job.value?.status === "PREVIEW_READY");
const isDone = computed(() => job.value?.status === "DONE");
const isFailed = computed(() => job.value?.status === "FAILED");

const currentStep = computed(() => {
  if (!job.value) return 0;
  switch (job.value.status) {
    case "UPLOADED":
    case "VALIDATING":
      return 1;
    case "PREVIEW_READY":
      return 2;
    case "IMPORTING":
      return 3;
    case "DONE":
      return 4;
    default:
      return 1;
  }
});

const progressPercent = computed(() => {
  const total = job.value?.total_rows || 0;
  if (!total) return job.value?.status === "UPLOADED" ? 0 : 5;
  return Math.min(99, Math.round(((job.value.processed_rows || 0) * 100) / total));
});

const entityCount = (entity, status) => counts.value?.[entity]?.[status] || 0;
const entityTotal = (entity) => Object.values(counts.value?.[entity] || {}).reduce((sum, n) => sum + n, 0);
const totalOf = (status) => ENTITIES.reduce((sum, e) => sum + entityCount(e.key, status), 0);
const problemCount = (entity) =>
  entityCount(entity, "ERROR") + entityCount(entity, "DUPLICATE_IN_FILE") + entityCount(entity, "FAILED");

const visibleEntities = computed(() => ENTITIES.filter((e) => entityTotal(e.key) > 0));
const newRows = computed(() => totalOf("NEW"));
const autoCreatedText = computed(() => {
  const created = job.value?.summary?.auto_created || {};
  return [
    [created.categories_auto, "categories"],
    [created.units_auto, "units"],
    [created.hsn_auto, "HSN codes in the HSN Tax Master"],
  ]
    .filter(([count]) => count)
    .map(([count, label]) => `${count} ${label}`)
    .join(", ");
});

const statusOptions = computed(() => {
  const base = [{ value: "ALL", label: "All" }];
  const keys = isDone.value
    ? ["IMPORTED", "EXISTS", "DUPLICATE_IN_FILE", "ERROR", "FAILED"]
    : ["NEW", "EXISTS", "DUPLICATE_IN_FILE", "ERROR"];
  return base.concat(
    keys
      .filter((key) => entityCount(activeEntity.value, key) > 0)
      .map((key) => ({ value: key, label: `${STATUS[key].label} (${entityCount(activeEntity.value, key)})` }))
  );
});

const tableColumns = computed(() => {
  const fields = columnsByEntity.value[activeEntity.value] || [];
  return [
    { title: "Row", dataIndex: "row_no", key: "row_no", width: 70, fixed: "left" },
    { title: "Status", key: "status", width: 150, fixed: "left" },
    { title: "Message", key: "message", width: 280 },
    ...fields.map((f) => ({
      title: f.required ? `${f.title} *` : f.title,
      key: f.field,
      field: f.field,
      ellipsis: true,
      width: Math.max(120, f.title.length * 10),
    })),
  ];
});

// ----- helpers ---------------------------------------------------------
const errorText = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join(", ");
  return fallback;
};

const formatCell = (value) => {
  if (value === true) return "Yes";
  if (value === false) return "No";
  if (value === null || value === undefined || value === "") return "";
  return value;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "");

const jobStatusLabel = (status) =>
  ({
    UPLOADED: "Queued",
    VALIDATING: "Checking",
    PREVIEW_READY: "Ready to import",
    IMPORTING: "Importing",
    DONE: "Completed",
    FAILED: "Failed",
  }[status] || status);

const jobStatusColor = (status) =>
  ({ PREVIEW_READY: "gold", DONE: "green", FAILED: "red", IMPORTING: "processing", VALIDATING: "processing" }[
    status
  ] || "default");

// ----- data loading ----------------------------------------------------
async function loadRecentJobs() {
  try {
    recentJobs.value = await listImportJobs(10);
  } catch {
    recentJobs.value = [];
  }
}

async function loadRows() {
  if (!job.value || !(isPreview.value || isDone.value)) return;
  rowsLoading.value = true;
  try {
    const data = await getImportRows(job.value.jobid, {
      entity: activeEntity.value,
      status: statusFilter.value === "ALL" ? undefined : statusFilter.value,
      search: search.value || undefined,
      page: pagination.current,
      page_size: pagination.pageSize,
    });
    rows.value = data.items;
    pagination.total = data.total;
  } catch (error) {
    rows.value = [];
    Swal.fire("Error", errorText(error, "Could not load the rows."), "error");
  } finally {
    rowsLoading.value = false;
  }
}

function pickDefaultEntity() {
  const withProblems = visibleEntities.value.find((e) => problemCount(e.key) > 0);
  activeEntity.value = (withProblems || visibleEntities.value[0] || ENTITIES[3]).key;
}

async function openJob(jobid, { silent = false } = {}) {
  try {
    const previousStatus = job.value?.status;
    job.value = await getImportJob(jobid);
    if (route.query.job !== jobid) router.replace({ query: { ...route.query, job: jobid } });

    if (isRunning.value) {
      schedulePoll();
      return;
    }
    if (previousStatus !== job.value.status || !silent) {
      pickDefaultEntity();
      statusFilter.value = "ALL";
      search.value = "";
      pagination.current = 1;
      await loadRows();
      loadRecentJobs();
    }
  } catch (error) {
    if (!silent) Swal.fire("Error", errorText(error, "Could not open this import."), "error");
    resetToUpload();
  }
}

function schedulePoll() {
  clearTimeout(pollTimer);
  pollTimer = setTimeout(() => {
    if (job.value) openJob(job.value.jobid, { silent: true });
  }, 1500);
}

// ----- actions ---------------------------------------------------------
function beforeUpload(file) {
  const ok = /\.(xlsx|xls|csv)$/i.test(file.name);
  if (!ok) {
    Swal.fire("Unsupported file", "Upload an Excel (.xlsx / .xls) or CSV file.", "warning");
    return false;
  }
  if (file.size > 20 * 1024 * 1024) {
    Swal.fire("File too large", "The file is larger than 20 MB. Split it into smaller files.", "warning");
    return false;
  }
  fileList.value = [file];
  csvEntity.value = null;
  return false;
}

function removeFile() {
  fileList.value = [];
  csvEntity.value = null;
}

async function onDownloadTemplate() {
  downloadingTemplate.value = true;
  try {
    await downloadTemplate(templateScope.value);
  } catch (error) {
    Swal.fire("Error", "Could not download the template.", "error");
  } finally {
    downloadingTemplate.value = false;
  }
}

async function onUpload() {
  if (!selectedFile.value) return;
  if (isCsv.value && !csvEntity.value) {
    Swal.fire("Choose the data type", "Tell us what the CSV file contains.", "info");
    return;
  }
  uploading.value = true;
  uploadPercent.value = 0;
  try {
    const created = await uploadImportFile(selectedFile.value, isCsv.value ? csvEntity.value : null, (p) => {
      uploadPercent.value = p;
    });
    fileList.value = [];
    job.value = created;
    router.replace({ query: { ...route.query, job: created.jobid } });
    schedulePoll();
  } catch (error) {
    Swal.fire("Upload failed", errorText(error, "The file could not be uploaded."), "error");
  } finally {
    uploading.value = false;
  }
}

async function onStartImport() {
  const summary = ENTITIES.filter((e) => entityCount(e.key, "NEW") > 0)
    .map((e) => `<li>${e.label}: <b>${entityCount(e.key, "NEW")}</b></li>`)
    .join("");
  const skipped = totalOf("EXISTS") + totalOf("DUPLICATE_IN_FILE") + totalOf("ERROR");
  const result = await Swal.fire({
    title: `Import ${newRows.value} new rows?`,
    html: `<ul style="text-align:left">${summary}</ul>${
      skipped ? `<p class="text-muted">${skipped} rows (existing, duplicates or errors) will be skipped.</p>` : ""
    }`,
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Import",
    confirmButtonColor: "#198754",
  });
  if (!result.isConfirmed) return;
  busy.value = true;
  try {
    job.value = await startImport(job.value.jobid);
    schedulePoll();
  } catch (error) {
    Swal.fire("Error", errorText(error, "The import could not be started."), "error");
  } finally {
    busy.value = false;
  }
}

async function onDownloadProblems() {
  try {
    await downloadProblemRows(job.value.jobid);
  } catch {
    Swal.fire("Error", "Could not download the skipped rows.", "error");
  }
}

async function onDiscard() {
  const result = await Swal.fire({
    title: "Discard this upload?",
    text: "Nothing has been imported yet. The checked rows will be removed.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonText: "Discard",
    confirmButtonColor: "#dc3545",
  });
  if (!result.isConfirmed) return;
  try {
    await discardImportJob(job.value.jobid);
    resetToUpload();
  } catch (error) {
    Swal.fire("Error", errorText(error, "Could not discard this upload."), "error");
  }
}

function resetToUpload() {
  clearTimeout(pollTimer);
  job.value = null;
  rows.value = [];
  fileList.value = [];
  csvEntity.value = null;
  const query = { ...route.query };
  delete query.job;
  router.replace({ query });
  loadRecentJobs();
}

function onTableChange(pager) {
  pagination.current = pager.current;
  pagination.pageSize = pager.pageSize;
  loadRows();
}

function onSearch() {
  pagination.current = 1;
  loadRows();
}

watch([activeEntity, statusFilter], () => {
  pagination.current = 1;
  loadRows();
});
watch(activeEntity, () => {
  statusFilter.value = "ALL";
});

onMounted(async () => {
  try {
    columnsByEntity.value = await getColumns();
  } catch (error) {
    if (error?.response?.status === 403) {
      Swal.fire("No access", "You need permission to add items, customers and suppliers to import data.", "warning");
    }
  }
  loadRecentJobs();
  if (route.query.job) openJob(String(route.query.job));
});

onBeforeUnmount(() => clearTimeout(pollTimer));
</script>

<template>
  <div class="main-wrapper">
    <layouts></layouts>
    <sidebar></sidebar>

    <div class="page-wrapper">
      <div class="content import-page">
        <pageaddheader
          :title="'DATA IMPORT'"
          :title1="'Load categories, suppliers, customers and items (with opening stock) from Excel'"
        />

        <div class="card shadow-sm mb-3">
          <div class="card-body py-3">
            <a-steps :current="currentStep" size="small">
              <a-step title="Upload" />
              <a-step title="Check" />
              <a-step title="Review" />
              <a-step title="Import" />
              <a-step title="Done" />
            </a-steps>
          </div>
        </div>

        <!-- Upload -->
        <template v-if="!job">
          <div class="row g-3">
            <div class="col-lg-8">
              <div class="card shadow-sm h-100">
                <div class="card-body p-4">
                  <div class="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
                    <div>
                      <h6 class="fw-bold mb-1">Upload your file</h6>
                      <p class="text-muted small mb-0">
                        One Excel workbook with Categories, Suppliers, Customers and Items sheets, or a CSV for one of them.
                      </p>
                    </div>
                    <div class="d-flex align-items-center gap-2">
                      <a-select v-model:value="templateScope" size="small" style="width: 170px">
                        <a-select-option value="all">All data</a-select-option>
                        <a-select-option v-for="e in ENTITIES" :key="e.key" :value="e.key">{{ e.label }} only</a-select-option>
                      </a-select>
                      <button class="btn btn-outline-primary btn-sm" :disabled="downloadingTemplate" @click="onDownloadTemplate">
                        <i class="fa fa-download me-1"></i> Download template
                      </button>
                    </div>
                  </div>

                  <a-upload-dragger
                    :file-list="fileList"
                    :before-upload="beforeUpload"
                    :max-count="1"
                    accept=".xlsx,.xls,.csv"
                    @remove="removeFile"
                  >
                    <p class="ant-upload-drag-icon mb-2"><i class="fa fa-file-excel fa-3x text-success"></i></p>
                    <p class="ant-upload-text">Click or drag a file here</p>
                    <p class="ant-upload-hint">.xlsx, .xls or .csv, up to 20 MB and 50,000 rows per sheet</p>
                  </a-upload-dragger>

                  <div v-if="isCsv" class="mt-3">
                    <label class="form-label small fw-semibold">This CSV contains</label>
                    <a-select v-model:value="csvEntity" placeholder="Choose data type" style="width: 240px">
                      <a-select-option v-for="e in ENTITIES" :key="e.key" :value="e.key">{{ e.label }}</a-select-option>
                    </a-select>
                  </div>

                  <div class="mt-4 d-flex align-items-center gap-3">
                    <button class="btn btn-success px-4" :disabled="!selectedFile || uploading" @click="onUpload">
                      <i class="fa fa-upload me-2"></i>{{ uploading ? "Uploading..." : "Upload and check" }}
                    </button>
                    <a-progress v-if="uploading" :percent="uploadPercent" size="small" style="max-width: 260px" />
                  </div>
                  <p class="text-muted small mt-3 mb-0">
                    Nothing is saved until you review the rows and press <b>Import</b>.
                  </p>
                </div>
              </div>
            </div>

            <div class="col-lg-4">
              <div class="card shadow-sm h-100 info-box">
                <div class="card-body p-4">
                  <h6 class="fw-bold mb-2"><i class="fa fa-info-circle me-1 text-primary"></i> How it works</h6>
                  <ul class="small text-muted ps-3 mb-0">
                    <li>Duplicates are skipped automatically: same name, mobile/phone, GSTIN, item code or barcode.</li>
                    <li>Rows already in your data are shown as <b>Already exists</b> and are not changed.</li>
                    <li><b>Opening Qty</b> becomes the opening stock of the current location.</li>
                    <li>For several batches of one item, repeat the row with a different Batch No.</li>
                    <li>The template has dropdowns with your categories, units and HSN codes, so names match exactly.</li>
                    <li>Missing categories and units are created for you.</li>
                    <li>GST: an HSN Code takes its rate from the HSN Tax Master (new codes are added with the row's Tax Percent); a Tax Percent alone is a manual rate.</li>
                    <li>Rows with errors are skipped; download them, fix and upload again.</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div v-if="recentJobs.length" class="card shadow-sm mt-3">
            <div class="card-body p-4">
              <h6 class="fw-bold mb-3">Recent imports</h6>
              <a-table
                :data-source="recentJobs"
                row-key="jobid"
                size="small"
                :pagination="false"
                :columns="[
                  { title: 'File', dataIndex: 'file_name', key: 'file' },
                  { title: 'Uploaded', key: 'created' },
                  { title: 'Rows', dataIndex: 'total_rows', key: 'rows', width: 90 },
                  { title: 'Status', key: 'status', width: 150 },
                  { title: '', key: 'open', width: 90 },
                ]"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'created'">{{ formatDate(record.created_at) }}</template>
                  <template v-else-if="column.key === 'status'">
                    <a-tag :color="jobStatusColor(record.status)">{{ jobStatusLabel(record.status) }}</a-tag>
                  </template>
                  <template v-else-if="column.key === 'open'">
                    <a href="javascript:void(0)" @click="openJob(record.jobid)">Open</a>
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </template>

        <!-- Running -->
        <div v-else-if="isRunning" class="card shadow-sm">
          <div class="card-body p-5 text-center">
            <h5 class="fw-bold mb-1">
              {{ job.status === "IMPORTING" ? "Importing your data..." : "Checking your file..." }}
            </h5>
            <p class="text-muted mb-4">
              {{ job.file_name }} ·
              {{ job.processed_rows || 0 }} / {{ job.total_rows || "?" }} rows. You can leave this page; the work continues.
            </p>
            <a-progress :percent="progressPercent" status="active" style="max-width: 520px" />
          </div>
        </div>

        <!-- Failed -->
        <div v-else-if="isFailed" class="card shadow-sm">
          <div class="card-body p-4">
            <a-alert type="error" show-icon message="This file could not be imported" :description="job.error" />
            <div class="mt-3 d-flex gap-2">
              <button class="btn btn-primary" @click="resetToUpload">Upload another file</button>
              <button v-if="entityTotal('item') || entityTotal('customer')" class="btn btn-outline-secondary" @click="onDownloadProblems">
                Download rows
              </button>
            </div>
          </div>
        </div>

        <!-- Review / Done -->
        <template v-else-if="isPreview || isDone">
          <a-alert
            v-if="isDone"
            class="mb-3"
            type="success"
            show-icon
            :message="`Import completed: ${totalOf('IMPORTED')} rows added`"
          >
            <template #description>
              <span v-if="totalOf('FAILED')" class="text-danger me-2">{{ totalOf("FAILED") }} rows failed to save. </span>
              <span v-if="autoCreatedText" class="me-2">Auto-created {{ autoCreatedText }}.</span>
              Open:
              <router-link v-for="e in ENTITIES" :key="e.key" :to="e.link" class="me-2">{{ e.label }}</router-link>
            </template>
          </a-alert>

          <div class="row g-3 mb-3">
            <div v-for="e in ENTITIES" :key="e.key" class="col-6 col-lg-3">
              <div
                class="card shadow-sm h-100 entity-card"
                :class="{ active: activeEntity === e.key, empty: !entityTotal(e.key) }"
                @click="entityTotal(e.key) && (activeEntity = e.key)"
              >
                <div class="card-body p-3">
                  <div class="d-flex justify-content-between">
                    <span class="fw-bold">{{ e.label }}</span>
                    <span class="text-muted small">{{ entityTotal(e.key) }} rows</span>
                  </div>
                  <div v-if="!entityTotal(e.key)" class="text-muted small mt-2">Not in file</div>
                  <div v-else class="small mt-2 lh-lg">
                    <template v-if="isDone">
                      <a-tag color="success">{{ entityCount(e.key, "IMPORTED") }} imported</a-tag>
                    </template>
                    <template v-else>
                      <a-tag color="green">{{ entityCount(e.key, "NEW") }} new</a-tag>
                    </template>
                    <a-tag v-if="entityCount(e.key, 'EXISTS')" color="blue">{{ entityCount(e.key, "EXISTS") }} exist</a-tag>
                    <a-tag v-if="entityCount(e.key, 'DUPLICATE_IN_FILE')" color="orange">
                      {{ entityCount(e.key, "DUPLICATE_IN_FILE") }} duplicate
                    </a-tag>
                    <a-tag v-if="entityCount(e.key, 'ERROR')" color="red">{{ entityCount(e.key, "ERROR") }} error</a-tag>
                    <a-tag v-if="entityCount(e.key, 'FAILED')" color="error">{{ entityCount(e.key, "FAILED") }} failed</a-tag>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="card shadow-sm">
            <div class="card-body p-3">
              <div class="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
                <div>
                  <span class="fw-bold">{{ job.file_name }}</span>
                  <a-tag class="ms-2" :color="jobStatusColor(job.status)">{{ jobStatusLabel(job.status) }}</a-tag>
                </div>
                <div class="d-flex flex-wrap gap-2">
                  <button class="btn btn-outline-secondary btn-sm" @click="onDownloadProblems">
                    <i class="fa fa-file-excel me-1"></i> Download skipped rows
                  </button>
                  <template v-if="isPreview">
                    <button class="btn btn-outline-danger btn-sm" @click="onDiscard">Discard</button>
                    <button class="btn btn-success btn-sm px-3" :disabled="!newRows || busy" @click="onStartImport">
                      <i class="fa fa-check me-1"></i> Import {{ newRows }} new rows
                    </button>
                  </template>
                  <button v-else class="btn btn-primary btn-sm" @click="resetToUpload">New import</button>
                </div>
              </div>

              <a-tabs v-model:activeKey="activeEntity">
                <a-tab-pane v-for="e in visibleEntities" :key="e.key">
                  <template #tab>
                    {{ e.label }}
                    <a-badge
                      v-if="problemCount(e.key)"
                      :count="problemCount(e.key)"
                      :number-style="{ backgroundColor: '#dc3545' }"
                      class="ms-1"
                    />
                  </template>
                </a-tab-pane>
              </a-tabs>

              <div class="d-flex flex-wrap justify-content-between gap-2 mb-2">
                <a-radio-group v-model:value="statusFilter" button-style="solid" size="small">
                  <a-radio-button v-for="opt in statusOptions" :key="opt.value" :value="opt.value">
                    {{ opt.label }}
                  </a-radio-button>
                </a-radio-group>
                <a-input-search
                  v-model:value="search"
                  placeholder="Search rows"
                  size="small"
                  style="width: 240px"
                  allow-clear
                  @search="onSearch"
                />
              </div>

              <a-table
                :columns="tableColumns"
                :data-source="rows"
                :loading="rowsLoading"
                :pagination="{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ['25', '50', '100', '200'],
                  showTotal: (total) => `${total} rows`,
                }"
                :scroll="{ x: 'max-content', y: 520 }"
                row-key="id"
                size="small"
                bordered
                @change="onTableChange"
              >
                <template #bodyCell="{ column, record }">
                  <template v-if="column.key === 'status'">
                    <a-tag :color="STATUS[record.status]?.color">{{ STATUS[record.status]?.label || record.status }}</a-tag>
                  </template>
                  <template v-else-if="column.key === 'message'">
                    <span :class="{ 'text-danger': ['ERROR', 'FAILED'].includes(record.status) }">{{ record.message }}</span>
                  </template>
                  <template v-else-if="column.field">
                    {{ formatCell(record.data[column.field]) }}
                  </template>
                </template>
              </a-table>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.import-page {
  max-width: 1400px;
  margin: auto;
}
.info-box {
  background-color: #f8faff;
  border: 1px solid #e6ecf1;
}
.entity-card {
  cursor: pointer;
  border: 2px solid transparent;
  transition: border-color 0.2s ease;
}
.entity-card.active {
  border-color: #1b2850;
}
.entity-card.empty {
  cursor: default;
  opacity: 0.6;
}
</style>
